import { PolygonLayer } from "@deck.gl/layers";
import { screenFlood, type FloodTerrain } from "./map/floodScenario";
import type { RoofEvidence } from "./map/roofGeometry";
import { FloodScenarioControl } from "./components/FloodScenarioControl";
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState, memo } from "react";
import DeckGL from "@deck.gl/react";
import { FlyToInterpolator, type Layer, type ControllerProps } from "@deck.gl/core";
import { Map as MapLibreMap, Source, Layer as MapLayer } from "react-map-gl/maplibre";
import * as maplibregl from "maplibre-gl";
import maplibreWorkerUrl from "maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url";
import type { MapOptions } from "maplibre-gl";
import type { FeatureCollection, LineString, Point, Polygon, MultiPolygon } from "geojson";
import { SOURCE_CATALOG, YALA } from "@yala/shared";
import type {
  AcademicSnapshot,
  AirQualityPoint,
  AqicnStation,
  CampusZoneProperties,
  ExecutiveSnapshot,
  IncidentFeature,
  IntelligenceItem,
  MarketSnapshot,
  PrecipNowcast,
  WeatherSnapshot,
  GistdaPoi,
  GistdaSolarBuilding,
  GistdaLandUse,
  ConflictIncident,
  SecurityStatus,
  FloodGauge,
  DamStatus,
  PattaniRiverForecast,
  Shelter,
  AirDangerProvince,
  YalaFloodOps,
  SecurityNewsItem,
  SecurityTimeline,
  SecurityDigest,
} from "@yala/shared";

import { useFeed } from "./hooks/useFeed";
import { buildTrafficSamples, type RoadProps } from "./sim/trafficSim";
import {
  buildingRoofsLayer,
  buildingsLayer,
  campusBoundaryLayer,
  cctvLayer,
  devicePresenceLayer,
  esriSatelliteLayer,
  himawariInfraredLayer,
  openTopoTerrainLayer,
  incidentLayer,
  trafficHeatmapLayer,
  transitStationsLayer,
  transitLinesLayer,
  roadNetworkLayer,
  datagoPointsLayer,
  distanceGridLayer,
  distanceGridLabelsLayer,
  civicPointsLayer,
  waterwaysLayer,
  templeSpiresLayer,
  oldTownDistrictLayer,
  type HeritageFeatureProps,
  CIVIC_PALETTE,
  type CivicKind,
  type DatagoPoint,
  type BuildingProperties,
  type CctvCamera,
  type ShuttleVehicle,
  type StationProps,
  type TransitLineProps,
  type ClassifiedRoadProps,
  gistdaPoiLayer,
  air4thaiLayer,
  gistdaSolarLayer,
  gistdaLandUseLayer,
  newsPinsLayer,
  ringRoadsLayer,
  riverBufferLayer,
  floodGaugesLayer,
  damStatusLayer,
  sheltersLayer,
  floodAffectedDistrictsLayer,
  conflictIncidentsLayer,
  conflictChoroplethLayer,
  povertyChoroplethLayer,
  securityNewsLayer,
  alphaEarthLandcoverLayer,
  alphaEarthFloodProneLayer,
  setMapTheme,
} from "./map/layers";
import { municipalFloodCameraLayer } from "./map/layers";
import type { MunicipalFloodCamera } from "./map/layers";
import { useTile3DLayer } from "./map/Tile3DLayer";
import { useGoogleTileSession } from "./hooks/useGoogleTileSession";
import { googleTileTemplate } from "./lib/googleTiles";
import { ALL_LAYERS, LENSES, layerCanEnable, enforceLayerExclusivity, exclusiveGroupOf, type LayerId, type LensId, type MapViewState } from "./map/presets";

import { TopBar } from "./components/TopBar";
import { MapCompass } from "./components/MapCompass";
import { BuildingLegend } from "./components/BuildingLegend";
import { LayerPalette, type LayerStatus } from "./components/LayerPalette";
import { KpiStrip } from "./components/KpiStrip";
import { NewsDesk } from "./components/NewsDesk";
import { FacebookPanel } from "./components/FacebookPanel";
import { WaterPanel, type ReservoirStatus } from "./components/WaterPanel";
import { ProvincialKPIs, type ProvincialKPIs as ProvincialKPIsType } from "./components/ProvincialKPIs";
import { EarthAlphaBrief } from "./components/EarthAlphaBrief";
import { SecurityDesk } from "./components/SecurityDesk";
import { FloodBrief } from "./components/FloodBrief";
import { AboutSheet } from "./components/AboutSheet";
import { CctvFloodBrief } from "./components/CctvFloodBrief";
import { CCTV_VIEWER_URL } from "./lib/cctvViewer";
import { APP_VERSION, APP_VERSION_LABEL } from "./lib/version";
import { YalaFloodOpsBrief } from "./components/YalaFloodOpsBrief";
import { AirDangerBrief } from "./components/AirDangerBrief";
// Heavy modals — lazy-loaded so they're excluded from the initial bundle.
// Each loads only on first open; subsequent opens are instant (module cached).
const SourceCatalog = lazy(() => import("./components/SourceCatalog").then((m) => ({ default: m.SourceCatalog })));
const Manual = lazy(() => import("./components/Manual").then((m) => ({ default: m.Manual })));
const Whitepaper = lazy(() => import("./components/Whitepaper").then((m) => ({ default: m.Whitepaper })));
const SheetsPanel = lazy(() => import("./components/SheetsPanel").then((m) => ({ default: m.SheetsPanel })));
const SituationDigest = lazy(() => import("./components/SituationDigest").then((m) => ({ default: m.SituationDigest })));
const AtlasView = lazy(() => import("./components/atlas/AtlasView").then((m) => ({ default: m.AtlasView })));
const PlatformView = lazy(() => import("./components/platform/PlatformView").then((m) => ({ default: m.PlatformView })));

// Inline the SheetsPanel URL check so we don't eagerly load the whole module.
const SHEETS_STORAGE_KEY = "yala:sheets-url-v1";
import { AqiBadge, type AqiTrend } from "./components/AqiBadge";
import { BuildingCard } from "./components/BuildingCard";
import { IncidentCard } from "./components/IncidentCard";
import { BuildingSearch } from "./components/BuildingSearch";
import { MapOverlayControls } from "./components/MapOverlayControls";
import { WorldStrip } from "./components/WorldStrip";
import { useWorldWeather } from "./hooks/useWorldWeather";
import { NewsTicker } from "./components/NewsTicker";
import { useSystemHealth } from "./hooks/useSystemHealth";
import { ActionBoard } from "./components/ActionBoard";
import { MayorsDesk } from "./components/MayorsDesk";
import { evaluateActionBoard } from "./lib/actionBoard";
import { buildDeskSignals } from "./lib/mayorDeskSignals";
import { MobileNav, type MobilePanel } from "./components/MobileNav";
import { ChatBox } from "./components/ChatBox";
import { PredictivePanel, METRIC_LAYER_MAP, METRIC_LABEL, type ForecastMetric } from "./components/PredictivePanel";
import { ExecutiveBriefing } from "./components/ExecutiveBriefing";
import { API_BASE } from "./lib/apiBase";
import type { NasaEarthReadings, FacebookPost } from "@yala/shared";
import { useDevicePresence } from "./hooks/useDevicePresence";
import { useIsMobile } from "./hooks/useMediaQuery";
import { useOnlineStatus } from "./hooks/useOnlineStatus";
import { useTheme } from "./hooks/useTheme";
import { useT } from "./hooks/useLang";
import { DemoTour, type TourScene } from "./components/DemoTour";
import { buildDistrictCentroids, resolveFloodAffectedPoints } from "./map/districtCentroids";

/**
 * Basemap — CARTO no-labels at the bottom, labels rendered on TOP via a
 * separate raster source so deck.gl context layers can interleave between
 * them. Theme-aware: dark → carto dark + deep navy ocean tint;
 * light → carto positron-light + pale sky-blue ocean tint.
 */
// Yesterday ISO date — GIBS tiles publish with ~24 h delay
const GIBS_DATE = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
const GIBS_BASE = "https://gibs.earthdata.nasa.gov/wmts/epsg3857/best";

function gibsUrl(product: string, level: number, format: "jpg" | "png"): string {
  return `${GIBS_BASE}/${product}/default/${GIBS_DATE}/GoogleMapsCompatible_Level${level}/{z}/{y}/{x}.${format}`;
}

// GIBS satellite layers rendered via MapLibre <Source>/<Layer> — MapLibre handles
// raster tile stretching perfectly (proven by the carto basemap).
// Deck.gl TileLayer is kept for esriSatelliteLayer only.
const GIBS_LAYERS: Array<{
  id: string;
  product: string;
  level: number;
  format: "jpg" | "png";
  opacity: number;
}> = [
  { id: "satellite-true-color",         product: "MODIS_Terra_CorrectedReflectance_TrueColor",    level: 9, format: "jpg", opacity: 0.85 },
  { id: "satellite-viirs-truecolor",     product: "VIIRS_NOAA20_CorrectedReflectance_TrueColor",   level: 9, format: "jpg", opacity: 0.85 },
  { id: "satellite-night",               product: "VIIRS_SNPP_DayNightBand_ENCC",                  level: 8, format: "png", opacity: 0.85 },
  { id: "satellite-imerg",               product: "IMERG_Precipitation_Rate",                      level: 6, format: "png", opacity: 0.75 },
  { id: "satellite-ndvi",                product: "MODIS_Terra_NDVI_8Day",                         level: 9, format: "png", opacity: 0.70 },
  { id: "satellite-lst",                 product: "MODIS_Terra_Land_Surface_Temp_Day",             level: 7, format: "png", opacity: 0.70 },
  { id: "satellite-aerosol",             product: "MODIS_Combined_Value_Added_AOD",                level: 7, format: "png", opacity: 0.70 },
  { id: "satellite-no2",                 product: "OMI_Nitrogen_Dioxide_Tropo_Column",             level: 6, format: "png", opacity: 0.70 },
  // NOTE: MODIS_Combined_Flood_3-Day is EPSG:4326-only on GIBS — it 400s on our
  // web-mercator (3857) map, so it's dropped. Flood is covered by the AlphaEarth
  // flood-prone layer + flood-risk polygons + river buffer + IMERG rainfall.
];

// Pre-computed, stable references for the React-rendered <Source>/<Layer> props.
// Without these, `tiles={[gibsUrl(...)]}` and `paint={{ "raster-opacity": ... }}`
// allocate a new array + object on every render, forcing react-map-gl to deep-diff
// raster sources on every map gesture.
const GIBS_RENDER: Array<{
  id: string;
  tiles: readonly [string];
  paint: { readonly "raster-opacity": number };
  maxzoom: number;
}> = GIBS_LAYERS.map((g) => ({
  id: g.id,
  tiles: [gibsUrl(g.product, g.level, g.format)] as const,
  paint: { "raster-opacity": g.opacity } as const,
  maxzoom: g.level,
}));

// Vite must emit the v6 module worker as its own asset.
maplibregl.setWorkerUrl(maplibreWorkerUrl);

type BasemapStyle = Exclude<NonNullable<MapOptions["style"]>, string>;

function basemapStyle(theme: "light" | "dark" | "inverted"): BasemapStyle {
  // `inverted` is a LIGHT theme (the page is white), so it takes the light
  // basemap — but the basemap is pushed back hard and the labels come forward.
  // The operator in high-contrast mode needs the data overlays to dominate, not
  // the street cartography. A full-strength basemap competes with the layers
  // that actually carry meaning.
  const isDark = theme === "dark";
  const isInverted = theme === "inverted";
  const baseSlug = isDark ? "World_Dark_Gray_Base" : "World_Light_Gray_Base";
  const labelsSlug = isDark ? "World_Dark_Gray_Reference" : "World_Light_Gray_Reference";
  // Yala is landlocked — no ocean. The background behind the raster tiles is a
  // neutral land tone (warm paper) so any gap between tiles reads as ground,
  // never a fake blue sea.
  const oceanBg = isDark ? "#14100f" : isInverted ? "#ffffff" : "#efe9ea";
  const baseOpacity = isDark ? 0.78 : isInverted ? 0.42 : 0.92;
  const labelOpacity = isDark ? 0.85 : isInverted ? 1.0 : 0.90;
  return {
    version: 8,
    sources: {
      "carto-base": {
        type: "raster",
        tiles: [
          `https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${baseSlug}/MapServer/tile/{z}/{y}/{x}`,
          `https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${baseSlug}/MapServer/tile/{z}/{y}/{x}`,
          `https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${baseSlug}/MapServer/tile/{z}/{y}/{x}`,
        ],
        tileSize: 256,
        attribution: "Esri, HERE, Garmin · © OpenStreetMap",
        maxzoom: 16,
      },
      "carto-labels": {
        type: "raster",
        tiles: [
          `https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${labelsSlug}/MapServer/tile/{z}/{y}/{x}`,
          `https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${labelsSlug}/MapServer/tile/{z}/{y}/{x}`,
          `https://services.arcgisonline.com/ArcGIS/rest/services/Canvas/${labelsSlug}/MapServer/tile/{z}/{y}/{x}`,
        ],
        tileSize: 256,
        maxzoom: 16,
      },
    },
    layers: [
      { id: "ocean-bg", type: "background", paint: { "background-color": oceanBg } },
      { id: "basemap", type: "raster", source: "carto-base", paint: { "raster-opacity": baseOpacity } },
      { id: "labels-top", type: "raster", source: "carto-labels", paint: { "raster-opacity": labelOpacity } },
    ],
  };
}

function normalizeProperties<T extends { features?: Array<{ properties?: object | null }> }>(data: T): T {
  if (data && Array.isArray(data.features)) {
    for (const f of data.features) {
      if (f.properties === null || f.properties === undefined) {
        (f as Record<string, unknown>).properties = {};
      }
    }
  }
  return data;
}

function useGeoJson<T>(path: string) {
  const [data, setData] = useState<T | null>(null);
  useEffect(() => {
    const ctrl = new AbortController();
    fetch(path, { signal: ctrl.signal })
      .then((r) => r.json())
      .then((j) => {
        setData(normalizeProperties(j) as T);
      })
      .catch(() => {});
    return () => {
      ctrl.abort();
    };
  }, [path]);
  return data;
}


function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

const MemoTopBar = memo(TopBar);
const MemoLayerPalette = memo(LayerPalette);
const MemoKpiStrip = memo(KpiStrip);
const MemoWorldStrip = memo(WorldStrip);
const MemoNewsTicker = memo(NewsTicker);
const MemoPredictivePanel = memo(PredictivePanel);
const MemoExecutiveBriefing = memo(ExecutiveBriefing);

// ── Guided demo tour — scripted sequence of the strongest, all-live views.
// Drives lens + camera; narration is Thai-first (EN via the language toggle).
// Coordinates are real Yala (central roundabout ~[101.2831, 6.5425]).
const TOUR_SCENES: TourScene[] = [
  {
    id: "overview", lensId: "vibes", lng: 101.2831, lat: 6.5425, zoom: 16.2,
    titleEn: "Yala City — the circular city", titleTh: "เทศบาลนครยะลา — เมืองผังวงแหวน",
    bodyEn: "A planned city of concentric ring roads radiating from the central roundabout. 15,148 distinct footprints with complete flat/gabled roof geometry over high-resolution imagery. Roof shape and height are estimates, not surveyed photogrammetry.",
    bodyTh: "เมืองที่วางผังเป็นวงแหวนซ้อนกันจากวงเวียนกลางเมือง อาคาร 15,148 รูปทรงพร้อมหลังคาแบน/จั่วบนภาพดาวเทียมละเอียด รูปทรงและความสูงเป็นค่าประมาณ ไม่ใช่งานสำรวจสามมิติ",
  },
  {
    id: "flood", lensId: "flood", lng: 101.2780, lat: 6.5510, zoom: 14.7,
    titleEn: "Flood risk — Pattani River", titleTh: "ความเสี่ยงน้ำท่วม — แม่น้ำปัตตานี",
    bodyEn: "The city's headline risk. Explore connected low ground using real SRTM terrain and the mapped Pattani River. Adjust a relative river-rise scenario and compare exposed building centroids. This is coarse planning screening, not a calibrated flood forecast.",
    bodyTh: "ความเสี่ยงอันดับหนึ่งของเมือง สำรวจพื้นที่ต่ำที่เชื่อมถึงแม่น้ำปัตตานีจาก DEM จริง ปรับระดับน้ำสมมติและเทียบจุดกึ่งกลางอาคาร เป็นเครื่องมือสำรวจแผนคร่าว ๆ ไม่ใช่การพยากรณ์น้ำท่วมที่ปรับเทียบแล้ว",
  },
  {
    id: "security", lensId: "security", lng: 101.2831, lat: 6.5100, zoom: 12.4,
    titleEn: "Deep South security", titleTh: "ความมั่นคงชายแดนใต้",
    bodyEn: "Aggregate conflict intensity by province and district shown as a choropleth — never individual incident points — paired with live security news from multiple desks. The situation has eased steadily since 2013.",
    bodyTh: "ภาพรวมความรุนแรงเชิงพื้นที่รายจังหวัดและอำเภอ แสดงเป็นแผนที่ความเข้ม พร้อมข่าวความมั่นคงสดจากหลายสำนัก สถานการณ์คลี่คลายต่อเนื่องตั้งแต่ปี 2556",
  },
  {
    id: "environment", lensId: "environment", lng: 101.2831, lat: 6.5425, zoom: 13.8,
    titleEn: "Environment & air quality", titleTh: "สิ่งแวดล้อมและคุณภาพอากาศ",
    bodyEn: "High-resolution satellite imagery, live air-quality stations, waterways and land use, plus per-building rooftop solar potential — the green picture of the municipality.",
    bodyTh: "ภาพดาวเทียมความละเอียดสูง สถานีตรวจวัดคุณภาพอากาศแบบสด ลำน้ำและการใช้ที่ดิน พร้อมศักยภาพพลังงานแสงอาทิตย์รายอาคาร",
  },
  {
    id: "executive", lensId: "executive", lng: 101.2831, lat: 6.5425, zoom: 13.9,
    titleEn: "The executive picture", titleTh: "สรุปสำหรับผู้บริหาร",
    bodyEn: "The strategic overview — population, tourism, road safety, and vulnerable groups — all from real government data (data.go.th), for the mayor's decisions at a glance.",
    bodyTh: "ภาพรวมยุทธศาสตร์ของเมือง — ประชากร การท่องเที่ยว ความปลอดภัยทางถนน และกลุ่มเปราะบาง จากข้อมูลภาครัฐจริง (data.go.th) เพื่อการตัดสินใจของผู้บริหารในพริบตา",
  },
];

export default function App({ onFlip }: { onFlip?: () => void } = {}) {
  const { theme } = useTheme();
  const t = useT();
  const [tourActive, setTourActive] = useState(false);
  const mapStyle = useMemo(() => basemapStyle(theme), [theme]);
  const { health: systemHealth, error: systemHealthError } = useSystemHealth(60_000);
  // Municipal boundary — the Yala City Municipality perimeter (OSM extract).
  // Replaces the forked chula-campus.geojson, which held Chulalongkorn
  // University (Bangkok) geometry and was invisible in the Yala viewport.
  const campus = useGeoJson<FeatureCollection<Polygon | MultiPolygon, CampusZoneProperties>>(
    "/geo/yala/boundary.geojson",
  );
  const buildings = useGeoJson<FeatureCollection<Polygon | MultiPolygon, BuildingProperties>>(
    "/geo/yala/buildings.geojson?roof-model=20261030",
  );
  // Roads + heritage now use real Yala OSM extracts (scripts/extract-yala-osm.mjs).
  // Flood zones use /geo/yala/flood-risk.geojson. Legacy Chula/Chonburi fork
  // layers are archived under apps/web/archive/geo (outside public/, so it is not shipped) — see ARCHIVE.md.
  const roads = useGeoJson<FeatureCollection<LineString, RoadProps>>("/geo/yala/roads.geojson");
  const transitStations = useGeoJson<FeatureCollection<Point, StationProps>>("/geo/yala/transit-stations.geojson");
  const transitLines = useGeoJson<FeatureCollection<LineString, TransitLineProps>>("/geo/yala/transit-lines.geojson");

  // Hour + weekend state for the traffic simulation
  // Traffic model is anchored to the current local time (the hour-of-day /
  // weekday-weekend slider was removed — it only nudged the off-by-default
  // traffic-heatmap layer, so it read as a control that "did nothing"). The
  // modelled traffic now simply reflects "now".
  const hour = new Date().getHours();
  const isWeekend = (() => { const d = new Date().getDay(); return d === 0 || d === 6; })();

  // Lens + per-layer toggles
  const [lens, setLens] = useState<LensId>("operations");
  const [mapViewState, setMapViewState] = useState<MapViewState>({ kind: "lens", lensId: "operations" });
  const [enabledLayers, setEnabledLayers] = useState<Set<LayerId>>(
    () => enforceLayerExclusivity(LENSES.find((l) => l.id === "operations")!.layers.filter((id) => layerCanEnable(id))),
  );
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [whitepaperOpen, setWhitepaperOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [sheetsOpen, setSheetsOpen] = useState(false);
  const [atlasOpen, setAtlasOpen] = useState(false);
  const [platformOpen, setPlatformOpen] = useState(false);
  /** The Mayor's Desk — one click to the morning brief. */
  const [mayorsDeskOpen, setMayorsDeskOpen] = useState(false);
  const [sheetsConfigured, setSheetsConfigured] = useState(() => {
    try { return Boolean(localStorage.getItem(SHEETS_STORAGE_KEY)); } catch { return false; }
  });
  // Forecast alerts — metrics whose p50 has breached alertThreshold
  const [forecastAlerts, setForecastAlerts] = useState<Set<string>>(new Set());
  const [forecastMetrics, setForecastMetrics] = useState<ForecastMetric[]>([]);

  // Mobile: 1-column stack with bottom segmented nav to swap panels.
  const isMobile = useIsMobile();
  const online = useOnlineStatus();
  const [mobilePanel, setMobilePanel] = useState<MobilePanel>("map");

  // Controlled map viewState — needed so building-search and click can fly the camera.
  // Tight bounds: the mayor should never accidentally scroll to Bangkok.
  const [viewState, setViewState] = useState({
    ...YALA.defaultView,
    minZoom: 13,
    maxZoom: 20,
    transitionDuration: 0,
    maxBounds: YALA.outerBounds,
  });

  // Mirror viewState to a ref so high-frequency reads (click handler, deck.gl
  // event callback) don't depend on stale React closures and don't force callback
  // recreation on every pan/zoom frame.
  const viewStateRef = useRef(viewState);
  viewStateRef.current = viewState;

  // rAF-throttled viewState mirror — DeckGL fires onViewStateChange potentially
  // many times per frame (high-refresh trackpads fire pointer events at 240 Hz+).
  // We coalesce those into ONE React state update per animation frame so the
  // ~1300-line App component re-renders at most 60 Hz, regardless of gesture rate.
  // The ref stays current immediately so any synchronous reader sees the latest.
  const pendingViewStateRef = useRef<typeof viewState | null>(null);
  const rafScheduledRef = useRef(false);
  const handleViewStateChange = useCallback(({ viewState: vs, interactionState }: { viewState: Record<string, unknown>; interactionState?: { inTransition?: boolean; isDragging?: boolean; isPanning?: boolean; isZooming?: boolean; isRotating?: boolean } }) => {
    // A programmatic flight already has its target in controlled state. Mirroring
    // its first animation frame with duration 0 cancels it before it can fly.
    if (interactionState?.inTransition && !interactionState.isDragging && !interactionState.isPanning && !interactionState.isZooming && !interactionState.isRotating) return;
    const longitude = vs.longitude as number;
    const latitude = vs.latitude as number;
    const zoom = vs.zoom as number;
    const pitch = vs.pitch as number;
    const bearing = vs.bearing as number;
    const next = {
      ...viewStateRef.current,
      longitude, latitude, zoom, pitch, bearing,
      transitionDuration: 0,
    };
    pendingViewStateRef.current = next;
    viewStateRef.current = next;
    if (rafScheduledRef.current) return;
    rafScheduledRef.current = true;
    requestAnimationFrame(() => {
      rafScheduledRef.current = false;
      const pending = pendingViewStateRef.current;
      pendingViewStateRef.current = null;
      if (pending) setViewState(pending);
    });
  }, []);

  // Selected building for the popup card.
  const [selectedBuilding, setSelectedBuilding] = useState<BuildingProperties | null>(null);
  // [lng, lat] where the building was picked — drives the Street View thumbnail.
  const [selectedCoord, setSelectedCoord] = useState<[number, number] | null>(null);
  // Selected incident — drives the IncidentCard with its "Open report ↗" link.
  const [selectedIncident, setSelectedIncident] = useState<IncidentFeature | null>(null);

  // Camera helpers
  const flyTo = useCallback((longitude: number, latitude: number, zoom = 17) => {
    setViewState((prev) => ({
      ...prev,
      longitude,
      latitude,
      zoom,
      transitionInterpolator: new FlyToInterpolator(),
      transitionDuration: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? 0 : 1800,
    }));
  }, []);

  // Reset map rotation to true north (compass click).
  const resetNorth = useCallback(() => {
    setViewState((prev) => ({ ...prev, bearing: 0, transitionDuration: 500 }));
  }, []);

  // Live lat/long readout under the cursor. Written imperatively to a DOM ref so
  // pointer-rate updates (60–240 Hz) never re-render the ~1300-line App. deck.gl
  // already runs a hover pick for getTooltip, so onHover adds no extra cost.
  const coordRef = useRef<HTMLSpanElement>(null);
  const handleMapHover = useCallback((info: { coordinate?: number[] }) => {
    const el = coordRef.current;
    if (!el) return;
    const c = info?.coordinate;
    if (c && c.length >= 2) {
      const [lng, lat] = c;
      el.textContent =
        `${Math.abs(lat).toFixed(5)}°${lat >= 0 ? "N" : "S"}  ` +
        `${Math.abs(lng).toFixed(5)}°${lng >= 0 ? "E" : "W"}`;
    } else {
      el.textContent = "—";
    }
  }, []);
  // handleMapClick reads viewState from ref to keep the callback identity stable —
  // before this change it was recreated on every pan/zoom frame because viewState
  // coords were in the deps array, forcing deck.gl to rebind its onClick prop.
  const handleMapClick = useCallback((info: { layer?: { id?: string } | null; object?: unknown; coordinate?: number[] }) => {
    if ((info.layer?.id === "municipality-buildings" || info.layer?.id === "campus-buildings") && info.object) {
      const f = info.object as { properties: BuildingProperties; geometry: { coordinates: number[][][] | number[][][][] } };
      setSelectedIncident(null);
      setSelectedBuilding(f.properties);
      const vs = viewStateRef.current;
      const [lng, lat] = info.coordinate ?? [vs.longitude, vs.latitude];
      setSelectedCoord([lng, lat]);
      flyTo(lng, lat, Math.max(vs.zoom, 17));
    } else if ((info.layer?.id === "incidents-city-reports" || info.layer?.id === "incidents-itic") && info.object) {
      // Click an incident → open its card with a deep-link back to the report.
      setSelectedBuilding(null);
      setSelectedIncident(info.object as IncidentFeature);
    } else if (!info.layer) {
      setSelectedBuilding(null);
      setSelectedIncident(null);
    }
  }, [flyTo]);

  const zoomBy = (delta: number) => {
    setViewState((prev) => ({
      ...prev,
      zoom: Math.max(prev.minZoom ?? 3, Math.min(prev.maxZoom ?? 20, prev.zoom + delta)),
      transitionDuration: 200,
    }));
  };

  // Device GPS presence — drives the on-map pulse.
  const { presence, request: requestDevice, clear: clearDevice } = useDevicePresence();
  const firstFixFlown = useRef(false);
  useEffect(() => {
    if (!firstFixFlown.current && presence.lng != null && presence.lat != null && presence.insideArea) {
      flyTo(presence.lng, presence.lat, 17);
      firstFixFlown.current = true;
    }
  }, [presence.lng, presence.lat, presence.insideArea, flyTo]);

  // View mode cycles 2D → 3D (buildings extrude) → 3DS (substructure: buildings
  // turn translucent, utilities drop to their burial depth). Camera follows.
  type ViewMode = "2D" | "3D" | "3DS";
  const [viewMode, setViewMode] = useState<ViewMode>("3D");
  const is3D = viewMode === "3D" || viewMode === "3DS";
  const isSubstructure = viewMode === "3DS";

  const cycleViewMode = useCallback(() => {
    setViewMode((prevMode) => {
      return prevMode === "2D" ? "3D" : prevMode === "3D" ? "3DS" : "2D";
    });
  }, []);

  useEffect(() => {
    setViewState((prev) => ({
      ...prev,
      pitch: viewMode === "2D" ? 0 : viewMode === "3D" ? 60 : 72,
      bearing: viewMode === "2D" ? 0 : viewMode === "3D" ? -18 : -28,
      transitionDuration: 700,
    }));
  }, [viewMode]);

  // ESC closes the topmost overlay.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (mayorsDeskOpen) setMayorsDeskOpen(false);
      else if (aboutOpen) setAboutOpen(false);
      else if (manualOpen) setManualOpen(false);
      else if (whitepaperOpen) setWhitepaperOpen(false);
      else if (catalogOpen) setCatalogOpen(false);
      else if (selectedIncident) setSelectedIncident(null);
      else if (selectedBuilding) setSelectedBuilding(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [aboutOpen, catalogOpen, mayorsDeskOpen, selectedBuilding, selectedIncident, manualOpen, whitepaperOpen]);

  // Lookup table for hover tooltips — keeps DeckGL declarative.
  const tooltipForPickMemo = useCallback((info: { layer?: { id?: string } | null; object?: unknown }) => {
    const o = info.object as Record<string, unknown> | undefined;
    if (!info.layer || !o) return null;
    const id = info.layer.id;
    const p = (o as { properties?: Record<string, unknown> }).properties ?? o;
    const pick = (...keys: string[]) => {
      for (const k of keys) {
        const v = (p as Record<string, unknown>)[k];
        if (typeof v === "string" && v.trim()) return v;
        if (typeof v === "number") return String(v);
      }
      return null;
    };
    let title: string | null = null;
    let sub: string | null = null;
    switch (id) {
      case "municipality-buildings":
      case "campus-buildings":
        title = pick("nameEn", "name", "nameTh") ?? "Building";
        sub = (() => {
          const lvls = (p as { levels?: number }).levels;
          const h = (p as { height?: number }).height;
          if (h) return `${h} m tall`;
          if (lvls) return `${lvls} floors`;
          return null;
        })();
        break;
      case "incidents-itic":
      case "incidents-city-reports":
        title = pick("title") ?? "Incident";
        sub = pick("category", "severity", "status");
        break;
      case "cctv-cameras":
        title = pick("name") ?? "CCTV";
        sub = pick("vendor");
        break;
      case "transit-stations":
        title = pick("name") ?? "Transit station";
        sub = `${pick("system") ?? ""} · ${pick("line") ?? ""}`;
        break;
      case "civic-points": {
        const kind = (p as { kind?: string }).kind ?? "other";
        const palette = CIVIC_PALETTE[kind as CivicKind] ?? CIVIC_PALETTE.other;
        const nm = pick("name:en", "name", "name:th") ?? palette.label;
        title = `${palette.glyph} ${nm}`;
        sub = palette.label + (pick("operator") ? ` · ${pick("operator")}` : "");
        break;
      }
      case "waterways": {
        const wt = (p as { waterway?: string }).waterway ?? "stream";
        const nm = pick("name:en", "name", "name:th") ?? wt;
        title = nm;
        sub = wt.toUpperCase() + (pick("intermittent") === "yes" ? " · intermittent" : "");
        break;
      }
      case "datago-points":
        title = pick("name", "nameEn") ?? "data.go.th POI";
        sub = `${(p as { category?: string }).category ?? ""} · ${(p as { source?: string }).source ?? ""}`;
        break;
      case "air4thai-stations": {
        const pm25 = (p as { pm25?: number | null }).pm25;
        const aqi = (p as { aqi?: number | null }).aqi;
        title = (p as { station?: string }).station ?? "Air4Thai station";
        sub = `PM2.5 ${pm25 ?? "—"} µg/m³ · AQI ${aqi ?? "—"} · PCD`;
        break;
      }
      case "gistda-pois":
        title = pick("name", "nameEn") ?? "GISTDA POI";
        sub = `${(p as { category?: string }).category ?? ""} · ${(p as { road?: string }).road ?? ""}`;
        break;
      case "gistda-solar":
        title = `Building #${pick("id") ?? ""}`;
        sub = `${(p as { solarIrr?: number }).solarIrr ?? "—"} kWh/m² · ${(p as { roofType?: string }).roofType ?? ""} · ${(p as { height?: number }).height ?? "—"} m`;
        break;
      case "news-pins": {
        const tags = (p as { tags?: string[] }).tags ?? [];
        title = pick("title") ?? "News";
        sub = `${tags.join(" ")} · ${pick("source") ?? ""}`;
        break;
      }
      case "gistda-landuse": {
        title = pick("name", "nameEn") ?? "Land parcel";
        sub = `${pick("code") ?? ""} · ${Math.round(Number((p as { area?: number }).area ?? 0))} m²`;
        break;
      }
      case "temple-spires-base":
      case "old-town-district":
        title = pick("name") ?? pick("nameTh") ?? "Heritage site";
        sub = pick("era") ?? null;
        break;
      case "flood-risk":
      case "flood-risk-zones":
        title = pick("name") ?? "Flood-prone area";
        sub = `${(pick("severity") ?? "").toUpperCase()} · ${pick("type") ?? ""}${pick("households") ? ` · ${pick("households")} households` : ""}`;
        break;
      case "ring-roads":
        title = pick("name", "nameEn", "nameTh") ?? "Road";
        sub = `${(p as { ring?: boolean }).ring ? "RING ROAD · " : ""}${pick("highway") ?? ""}`;
        break;
      case "river-buffer":
        title = pick("name") ?? "Pattani River flood corridor";
        sub = pick("bufferM") ? `${pick("bufferM")} m buffer` : null;
        break;
      case "conflict-incidents":
        title = `${(pick("eventType") ?? "incident").replace(/-/g, " ")}`;
        sub = `${pick("province") ?? ""}${pick("district") ? ` · ${pick("district")}` : ""} · ${(p as { fatalities?: number }).fatalities ?? 0} killed · ${pick("date") ?? ""}`;
        break;
      case "security-news": {
        const tags = (p as { tags?: string[] }).tags ?? [];
        title = pick("title") ?? "Security news";
        sub = `${tags.join(" ")} · ${pick("source") ?? ""}`;
        break;
      }
      case "flood-gauges":
        title = pick("name") ?? "River gauge";
        sub = `${(pick("status") ?? "").toUpperCase()} · ${pick("levelM") ?? "—"} m${pick("warningM") ? ` / ${pick("warningM")} m warn` : ""}`;
        break;
      case "dam-status":
        title = pick("name") ?? "Bang Lang Dam";
        sub = `${(pick("status") ?? "").toUpperCase()} · ${pick("storagePct") ?? "—"}% · ${pick("outflowCms") ?? "—"} m³/s`;
        break;
      case "shelters": {
        const s = p as Partial<Shelter>;
        // Amenity flags are data, and the pictographs that used to stand in
        // for them were colour emoji — a second hue in a one-hue instrument,
        // and a legend nobody could read. They are words now.
        const amenities = [
          s.hasPower ? "POWER" : null,
          s.hasWater ? "WATER" : null,
          s.hasToilet ? "WC" : null,
        ]
          .filter(Boolean).join(" · ");
        title = pick("name") ?? "Shelter";
        sub = `Capacity ${pick("capacityPersons") ?? "—"}${amenities ? ` · ${amenities}` : ""}${s.phone ? ` · ${s.phone}` : ""}`;
        break;
      }
      case "flood-affected-districts":
        title = `${pick("district") ?? "District"} · ${pick("villages") ?? "—"} affected villages`;
        sub = "Provincial flood situation report · data.go.th/yala-province";
        break;
      case "municipal-flood-cameras": {
        const c = p as Partial<MunicipalFloodCamera>;
        const kind = c.kind === "flood-shelter" ? "Flood shelter" : c.kind === "water-pump" ? "Water pump" : "Unclassified";
        title = c.nameTh ?? pick("id") ?? "Camera";
        sub = `${kind}${c.side ? ` · ${c.side === "river" ? "riverside" : "municipal side"}` : ""} · ${c.isLive ? "reporting" : "NOT REPORTING"}`;
        break;
      }
      case "alphaearth-landcover":
        title = pick("class") ?? "Land cover";
        sub = pick("note") ?? "AlphaEarth (derived)";
        break;
      case "alphaearth-floodprone":
        title = `Flood risk · ${(pick("risk") ?? "").toUpperCase()}`;
        sub = pick("note") ?? "AlphaEarth (derived)";
        break;
      default:
        return null;
    }
    return {
      html:
        `<div class="picker-tooltip"><div class="picker-title">${escapeHtml(title ?? "")}</div>` +
        (sub ? `<div class="picker-sub">${escapeHtml(sub)}</div>` : "") +
        `</div>`,
      style: {
        background: "transparent",
        border: "none",
        padding: "0",
      },
    };
  }, []);

  const onLensChange = useCallback((id: LensId) => {
    setLens(id);
    if (isMobile) setMobilePanel("map");
    setMapViewState({ kind: "lens", lensId: id });
    const next = LENSES.find((l) => l.id === id);
    if (next) setEnabledLayers(enforceLayerExclusivity(next.layers.filter((layerId) => layerCanEnable(layerId))));
    setForecastAlerts(new Set()); // clear stale alert badges on lens switch
    if (id === "vibes" || id === "flood") {
      setViewMode("3D");
      flyTo(id === "vibes" ? 101.2831 : 101.278, id === "vibes" ? 6.5425 : 6.551, id === "vibes" ? 16.2 : 14.7);
    }
  }, [flyTo, isMobile]);

  const handleForecastMetricClick = useCallback((metric: string) => {
    const entry = METRIC_LAYER_MAP[metric];
    if (!entry) return;
    setEnabledLayers((prev) => {
      const next = new Set(prev);
      entry.layers.forEach((id) => next.add(id));
      return enforceLayerExclusivity(next, entry.layers.find((id) => exclusiveGroupOf(id)));
    });
    if (entry.flyToCoast) flyTo(100.995, 13.352, 13.5);
  }, [flyTo]);

  const handleForecastAlert = useCallback((metric: string) => {
    setForecastAlerts((prev) => prev.has(metric) ? prev : new Set([...prev, metric]));
  }, []);

  // Guided-tour scene driver: switch lens, then fly the camera. Defined after
  // onLensChange + flyTo so it can reference them directly.
  const applyTourScene = useCallback((s: TourScene) => {
    onLensChange(s.lensId);
    setViewMode("3D");
    flyTo(s.lng, s.lat, s.zoom);
  }, [onLensChange, flyTo]);
  // Transient toast — gives a layer toggle visible feedback when it would
  // otherwise render nothing (no features yet, or a feed that needs an API key).
  // Kills the "I clicked it and nothing happened" embarrassment.
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);
  useEffect(() => () => { if (toastTimer.current) window.clearTimeout(toastTimer.current); }, []);

  // Latest layer feedback (counts + feed health), read by onToggleLayer without
  // making the callback depend on every feed poll. Assigned during render below.
  const layerFeedbackRef = useRef<{
    counts: Record<string, number>;
    statuses: Partial<Record<LayerId, LayerStatus>>;
    enabled: Set<LayerId>;
  }>({ counts: {}, statuses: {}, enabled: new Set() });

  const onToggleLayer = useCallback((id: LayerId) => {
    const fb = layerFeedbackRef.current;
    const turningOn = !fb.enabled.has(id);
    setEnabledLayers((prev) => {
      const next = new Set(prev);
      if (next.has(id)) { next.delete(id); return next; }
      next.add(id);
      // Base satellite + full-area colorizers are radio groups — the layer the
      // user just tapped wins, the rest of its group switches off.
      return enforceLayerExclusivity(next, id);
    });
    if (turningOn) {
      const label = ALL_LAYERS.find((l) => l.id === id)?.label ?? id;
      const group = exclusiveGroupOf(id);
      const removed = group ? group.filter((g) => g !== id && fb.enabled.has(g)) : [];
      const status = fb.statuses[id];
      const count = fb.counts[id];
      if (removed.length) {
        const names = removed.map((r) => ALL_LAYERS.find((l) => l.id === r)?.label ?? r).join(", ");
        showToast(`${label} — switched off ${names} (one overlay at a time keeps the map readable)`);
      } else if (status?.tier === "unavailable") {
        showToast(`${label} — no live data (${status.note ?? "needs API key"})`);
      } else if (count === 0) {
        showToast(`${label} — no features to show right now`);
      }
    }
  }, [showToast]);
  const restoreLens = useCallback(() => {
    const next = LENSES.find((l) => l.id === lens);
    setMapViewState({ kind: "lens", lensId: lens });
    if (next) setEnabledLayers(enforceLayerExclusivity(next.layers.filter((layerId) => layerCanEnable(layerId))));
  }, [lens]);
  const setAerialOnly = useCallback(() => {
    setViewMode("2D");
    setMapViewState({ kind: "custom", label: "Clean aerial view" });
    setEnabledLayers(new Set<LayerId>(["satellite-esri", "municipality-boundary-line"]));
  }, []);
  const clearOverlays = useCallback(() => {
    setViewMode("2D");
    setMapViewState({ kind: "custom", label: "Overlays hidden" });
    setEnabledLayers(new Set<LayerId>(["satellite-esri", "municipality-boundary-line"]));
  }, []);

  // Feeds
  const cityReports = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/city-reports`, 5 * 60_000);
  const iticEvents = useFeed<IncidentFeature>(`${API_BASE}/api/incidents/itic`, 3 * 60_000);
  const news = useFeed<IntelligenceItem>(`${API_BASE}/api/news`, 3 * 60_000);
  const weather = useFeed<WeatherSnapshot>(`${API_BASE}/api/weather`, 30 * 60_000);
  const airQuality = useFeed<AirQualityPoint>(`${API_BASE}/api/air-quality`, 15 * 60_000);
  const air4thai = useFeed<AirQualityPoint>(`${API_BASE}/api/air-quality/air4thai`, 30 * 60_000);
  // Real AQICN ground station nearest Yala (White Elephant Park) — headline AQI.
  const aqicn = useFeed<AqicnStation>(`${API_BASE}/api/air-quality/aqicn`, 15 * 60_000);
  const cctv = useFeed<CctvCamera>(`${API_BASE}/api/cctv/longdo`, 10 * 60_000);
  const aqiTrend = useFeed<AqiTrend>(`${API_BASE}/api/air-quality/trend`, 15 * 60_000);
  const executive = useFeed<ExecutiveSnapshot>(`${API_BASE}/api/executive`, 15 * 60_000);
  const markets = useFeed<MarketSnapshot>(`${API_BASE}/api/markets`, 10 * 60_000);
  const precip = useFeed<PrecipNowcast>(`${API_BASE}/api/precip-nowcast`, 5 * 60_000);
  const datago = useFeed<DatagoPoint>(`${API_BASE}/api/datago/points`, 30 * 60_000);
  const facebook = useFeed<FacebookPost>(`${API_BASE}/api/social/facebook`, 10 * 60_000);
  const reservoirs = useFeed<ReservoirStatus>(`${API_BASE}/api/datago/reservoirs?scope=yala-v2`, 60 * 60_000);
  const provincialKPIs = useFeed<ProvincialKPIsType>(`${API_BASE}/api/datago/provincial-kpis`, 6 * 60 * 60_000);
  const gistdaPois = useFeed<GistdaPoi>(`${API_BASE}/api/gistda/poi`, 60 * 60_000);
  const gistdaSolar = useFeed<GistdaSolarBuilding>(`${API_BASE}/api/gistda/solar`, 6 * 60 * 60_000);
  const gistdaLandUse = useFeed<GistdaLandUse>(`${API_BASE}/api/gistda/landuse`, 60 * 60_000);
  const nasaEarth = useFeed<NasaEarthReadings>(`${API_BASE}/api/nasa/earth-readings`, 6 * 60 * 60_000);
  // Shuttle and academic calendar not available in this deployment
  const shuttle = { data: [] as ShuttleVehicle[], fallbackTier: "unavailable" as const, ageMinutes: 0 };
  const academic = { data: [] as AcademicSnapshot[] };

  // Maritime infrastructure removed — Yala is landlocked (no port / ferry / nav-aids).

  // Deep South province + Yala district boundaries — for aggregate choropleths.
  const provinceBoundaries = useGeoJson<FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>>(
    "/geo/yala/boundaries/provinces.geojson",
  );
  const districtBoundaries = useGeoJson<FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>>(
    "/geo/yala/boundaries/districts.geojson",
  );

  // Civic POIs + waterways — Yala municipal OSM extract.
  const civicPoints = useGeoJson<FeatureCollection<Point, Record<string, unknown>>>(
    "/geo/yala/civic-pois.geojson",
  );
  const waterways = useGeoJson<FeatureCollection<LineString, Record<string, unknown>>>(
    "/geo/yala/waterways.geojson",
  );
  const heritage = useGeoJson<FeatureCollection<Point | Polygon | MultiPolygon, HeritageFeatureProps>>(
    "/geo/yala/heritage.geojson",
  );
  const terrain = useGeoJson<FloodTerrain>("/geo/yala/flood-terrain.json");
  const roofEvidence = useGeoJson<RoofEvidence>("/geo/yala/roof-evidence.json");
  const [scenarioRise, setScenarioRise] = useState(2);
  const floodScenario = useMemo(() => terrain ? screenFlood(terrain, scenarioRise, buildings) : null, [terrain, scenarioRise, buildings]);
  // Old hand-drawn boxes have no verified exposure counts. Keep them out of operations.


  // ── Yala-specific static GeoJSON ──────────────────────────────────────────
  const ringRoads = useGeoJson<FeatureCollection<LineString, Record<string, unknown>>>(
    "/geo/yala/ring-roads.geojson",
  );
  const riverBuffer = useGeoJson<FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>>(
    "/geo/yala/river-buffer.geojson",
  );
  // AlphaEarth EO layers — may be absent in some deployments; fail gracefully.
  const alphaLandcover = useGeoJson<FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>>(
    "/geo/yala/alphaearth/landcover.geojson",
  );
  const alphaFloodProne = useGeoJson<FeatureCollection<Polygon | MultiPolygon, Record<string, unknown>>>(
    "/geo/yala/alphaearth/floodprone.geojson",
  );

  // ── Yala live feeds (API; render nothing if the endpoint 404s) ────────────
  const conflictIncidents = useFeed<ConflictIncident>(`${API_BASE}/api/security/incidents`, 30 * 60_000);
  const securityStatus = useFeed<SecurityStatus>(`${API_BASE}/api/security/status`, 6 * 60 * 60_000);
  const securityFeed = useFeed<SecurityNewsItem>(`${API_BASE}/api/security/feed`, 3 * 60_000);
  const securityTimeline = useFeed<SecurityTimeline>(`${API_BASE}/api/security/timeline`, 30 * 60_000);
  const securityDigest = useFeed<SecurityDigest>(`${API_BASE}/api/security/digest`, 4 * 60 * 60_000);
  const floodGauges = useFeed<FloodGauge>(`${API_BASE}/api/flood/gauges`, 15 * 60_000);
  const damStatus = useFeed<DamStatus>(`${API_BASE}/api/flood/dam`, 30 * 60_000);
  const riverForecast = useFeed<PattaniRiverForecast>(`${API_BASE}/api/flood/river-forecast`, 15 * 60_000);
  const shelters = useFeed<Shelter>(`${API_BASE}/api/flood/shelters`, 6 * 60 * 60_000); // DDPM registry — changes rarely
  const floodCams = useFeed<MunicipalFloodCamera>(`${API_BASE}/api/cctv/municipal`, 5 * 60_000); // municipal pump + shelter camera presence
  const airDanger = useFeed<AirDangerProvince>(`${API_BASE}/api/airdash/deep-south`, 30 * 60_000);
  const floodOps = useFeed<YalaFloodOps>(`${API_BASE}/api/yala/flood-ops`, 30 * 60_000);
  // District centroids derived from the authoritative boundary geojson (already
  // loaded), keyed by Thai name — no hand-typed coordinates to drift.
  const districtCentroids = useMemo(
    () => buildDistrictCentroids(districtBoundaries?.features ?? [], [YALA.center[0], YALA.center[1]]),
    [districtBoundaries],
  );
  // Flood-affected villages → one map point per district. Unmatched districts are
  // surfaced (dev warning) rather than silently dropped, so a future rename is loud.
  const floodAffectedPoints = useMemo(() => {
    const { points, unmatched } = resolveFloodAffectedPoints(floodOps.data[0]?.affectedByDistrict ?? [], districtCentroids);
    if (import.meta.env.DEV && unmatched.length) {
      console.warn(`[flood-affected] no centroid for districts: ${unmatched.join(", ")} — dropped from map layer`);
    }
    return points;
  }, [floodOps.data, districtCentroids]);

  // Feeds the Action Board depends on. A dead feed becomes a "blind spot" item
  // so an empty board is never mistaken for good news.
  //
  // A feed counts as blind as soon as it is erroring with nothing loaded — NOT
  // only once useFeed's 3-failure counter flips it to "unavailable". On a 15-min
  // refresh that would leave the board silently blind for ~45 minutes, which is
  // the exact failure this panel exists to prevent.
  //
  // It also counts as blind while it is STILL LOADING and has never delivered a
  // reading. "Not reported yet" is not "reporting": a gauge we have never heard
  // from is a signal we are blind to. Without this the board sat on
  // "evaluating…" — an indefinite spinner — for up to 45 minutes whenever one
  // request timed out, because the gauges tier stays "loading" until the next
  // poll, while the board claimed it was watching that gauge.
  const feedOk = (f: { fallbackTier: string; error: string | null; loadedAt: string | null }) =>
    f.fallbackTier !== "unavailable" &&
    !(f.error != null && f.loadedAt == null) &&
    !(f.fallbackTier === "loading" && f.loadedAt == null);
  // "evaluating…" means NOTHING is known yet — every monitored feed is still
  // on its first load. It is not a per-feed spinner: as soon as one feed
  // resolves the board must show a definite state, because the old
  // gauges-only flag could keep it on "evaluating…" for 45 minutes.
  const boardStillLoading =
    floodGauges.loadedAt == null && floodGauges.error == null &&
    damStatus.loadedAt == null && damStatus.error == null &&
    precip.loadedAt == null && precip.error == null &&
    floodOps.loadedAt == null && floodOps.error == null;

  const actionBoardFeeds = useMemo(
    () => [
      { id: "gauges", label: { en: "river gauges", th: "สถานีวัดระดับน้ำ" }, ok: feedOk(floodGauges) },
      { id: "dam", label: { en: "Bang Lang Dam telemetry", th: "ข้อมูลเขื่อนบางลาง" }, ok: feedOk(damStatus) },
      { id: "precip", label: { en: "rain nowcast", th: "พยากรณ์ฝนระยะสั้น" }, ok: feedOk(precip) },
      { id: "flood-ops", label: { en: "provincial flood report", th: "รายงานอุทกภัยจังหวัด" }, ok: feedOk(floodOps) },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      floodGauges.fallbackTier, floodGauges.error, floodGauges.loadedAt,
      damStatus.fallbackTier, damStatus.error, damStatus.loadedAt,
      precip.fallbackTier, precip.error, precip.loadedAt,
      floodOps.fallbackTier, floodOps.error, floodOps.loadedAt,
    ],
  );

  // ── THE MAYOR'S DESK ────────────────────────────────────────────────────
  // One input for the action board, shared by the rail panel and the desk, so
  // the two can never disagree about what needs a decision.
  const boardInput = useMemo(
    () => ({
      dam: damStatus.data[0] ?? null,
      gauges: floodGauges.data,
      riverForecast: riverForecast.data[0] ?? null,
      precip: precip.data[0] ?? null,
      floodOps: floodOps.data[0] ?? null,
      airDanger: airDanger.data,
      feeds: actionBoardFeeds,
    }),
    [
      damStatus.data, floodGauges.data, riverForecast.data, precip.data,
      floodOps.data, airDanger.data, actionBoardFeeds,
    ],
  );

  const deskActions = useMemo(() => evaluateActionBoard(boardInput), [boardInput]);

  const deskSignals = useMemo(
    () =>
      buildDeskSignals({
        dam: boardInput.dam,
        gauges: boardInput.gauges,
        riverForecast: boardInput.riverForecast,
        precip: boardInput.precip,
        floodOps: boardInput.floodOps,
        airDanger: boardInput.airDanger,
        // The same ground station the dashboard's own AQI badge reads, so the
        // desk can never contradict the number already on screen.
        aqi: aqicn.data[0] ?? airQuality.data[0] ?? null,
        weather: weather.data[0] ?? null,
        conflicts: conflictIncidents.data,
        incidents: [
          ...cityReports.data.filter((r) => r.status !== "resolved"),
          ...iticEvents.data,
        ],
        actions: deskActions,
        ok: {
          flood: feedOk(floodGauges) && feedOk(damStatus),
          security: feedOk(conflictIncidents),
          weather: feedOk(weather),
          air: feedOk(aqicn) || feedOk(airQuality),
          incidents: feedOk(cityReports) || feedOk(iticEvents),
        },
        ages: {
          flood: floodGauges.ageMinutes,
          security: conflictIncidents.ageMinutes,
          weather: weather.ageMinutes,
          air: aqicn.ageMinutes ?? airQuality.ageMinutes,
          incidents: cityReports.ageMinutes,
        },
      }),
    [
      boardInput, aqicn.data, airQuality.data, weather.data, conflictIncidents.data,
      cityReports.data, iticEvents.data, deskActions,
      floodGauges.fallbackTier, floodGauges.error, floodGauges.loadedAt, floodGauges.ageMinutes,
      damStatus.fallbackTier, damStatus.error, damStatus.loadedAt,
      conflictIncidents.fallbackTier, conflictIncidents.error, conflictIncidents.loadedAt, conflictIncidents.ageMinutes,
      weather.fallbackTier, weather.error, weather.loadedAt, weather.ageMinutes,
      aqicn.fallbackTier, aqicn.error, aqicn.loadedAt, aqicn.ageMinutes,
      airQuality.fallbackTier, airQuality.error, airQuality.loadedAt, airQuality.ageMinutes,
      cityReports.fallbackTier, cityReports.error, cityReports.loadedAt, cityReports.ageMinutes,
      iticEvents.fallbackTier, iticEvents.error, iticEvents.loadedAt,
    ],
  );

  const worldWeather = useWorldWeather();
  const hostWeather = useMemo(() => {
    // Prefer browser-side Open-Meteo data (richer fields — and the only source
    // with a 5-day forecast).
    // The id was "cbo" (Chonburi) and matched nothing, so this always fell
    // through to the backend snapshot, which has no `daily` array — the 5-day
    // rain outlook in the world strip rendered nothing at all, into a track
    // that had reserved 280px for it.
    const city = worldWeather.find((c) => c.city.id === "yla");
    if (city) {
      const { city: _city, fetchedAt: _fetchedAt, ...rest } = city;
      return rest;
    }
    // Fall back to backend /api/weather when browser fetch fails (Safari, CSP, offline).
    const snap = weather.data?.[0];
    if (!snap) return null;
    return {
      tempC: snap.tempC,
      apparentTempC: snap.feelsLikeC,
      humidity: snap.humidity,
      rainNow: snap.precipMm,
      windKmh: snap.windKmh,
      windDeg: null,
      uv: null,
      cloudPct: null,
      pressurehPa: null,
      visKm: null,
      isDay: null,
      condition: snap.condition,
      sunrise: null,
      sunset: null,
      daily: [],
    };
  }, [worldWeather, weather.data]);

  const hostPulse = useMemo(() => {
    const oneDayAgo = Date.now() - 24 * 60 * 60 * 1000;
    const openReports = cityReports.data.filter((r) => r.status !== "resolved").length;
    const news24h = news.data.filter((n) => {
      const t = new Date(n.publishedAt).getTime();
      return !Number.isNaN(t) && t >= oneDayAgo;
    }).length;
    return {
      iticEvents: iticEvents.data.length,
      openReports,
      news24h,
      shuttleLive: shuttle.data.length,
    };
  }, [iticEvents.data, cityReports.data, news.data, shuttle.data]);

  // Host AQI/PM2.5 for the WorldStrip — prefer the real AQICN ground station so
  // it matches the headline AqiBadge (no "40 here, 19 there" contradiction);
  // fall back to the Open-Meteo trend model when the station is offline.
  const hostAqi = aqicn.data[0]?.aqi ?? aqiTrend.data[0]?.current.aqi ?? null;
  const hostPm25 = aqicn.data[0]?.pm25 ?? aqiTrend.data[0]?.current.pm25 ?? null;

  // Traffic samples — recomputed when hour or roads change
  const trafficSamples = useMemo(() => {
    if (!roads) return [];
    return buildTrafficSamples(roads, hour, { isWeekend });
  }, [roads, hour, isWeekend]);

  // Photorealistic 3D Tiles (Google) — must be called at top level (Rules of Hooks)
  const tile3d = useTile3DLayer({
    visible: enabledLayers.has("tile3d-buildings"),
    source: "google",
  });

  // Google Map Tiles sessions — minted lazily when the layer is first enabled.
  const googleSatSession = useGoogleTileSession("satellite", enabledLayers.has("google-satellite"));
  const googleTrafficSession = useGoogleTileSession("traffic", enabledLayers.has("google-traffic"));

  const showFloodScenario = enabledLayers.has("flood-risk-zones");
  const exposedBuildings = useMemo(() => new Set(showFloodScenario ? floodScenario?.exposedIds : []), [showFloodScenario, floodScenario]);
  // Pre-memoize the two largest layers (20,877 buildings each). The umbrella
  // `layers` memo below has ~40 deps including SWR feed polls — if any of those
  // change, the buildings/roofs GeoJsonLayer would otherwise be re-instantiated
  // and deck.gl would re-tessellate. With a stable reference, deck.gl sees the
  // same layer object and skips the diff entirely.
  const memoizedBuildingsLayer = useMemo<Layer | null>(
    () => buildings ? (buildingsLayer(buildings, { extruded: is3D, ghosted: isSubstructure, exposed: exposedBuildings }) as Layer) : null,
    [buildings, is3D, isSubstructure, exposedBuildings],
  );
  const memoizedRoofsLayer = useMemo<Layer | null>(
    () => {
      if (!buildings || !is3D || isSubstructure) return null;
      return buildingRoofsLayer(buildings, roofEvidence) as Layer;
    },
    [buildings, is3D, isSubstructure, roofEvidence],
  );

  const layers = useMemo<Layer[]>(() => {
    // Push the active theme into the deck.gl layer builders so outline/label
    // colors contrast against whichever basemap is showing. theme is in the
    // dep array below, so layers rebuild the instant the toggle flips.
    setMapTheme(theme === "dark");
    // Deck.gl paints in array order: earlier = beneath, later = on top. Without a
    // deliberate order, big translucent AREA FILLS (flood zones, EO polygons,
    // choropleths) end up above buildings/roads/markers and bury whatever a
    // toggle is meant to reveal — toggling a point layer then looks like a no-op.
    // So bucket every layer into z-tiers and concat bottom→top at the end:
    //   imagery → fills → lines → structures → points → top
    const imagery: Layer[] = [];     // raster basemaps
    const fills: Layer[] = [];       // area polygons: boundary fill, flood/EO zones, choropleths, land use
    const lines: Layer[] = [];       // roads, rivers, ring roads, transit lines, distance rings
    const structures: Layer[] = [];  // extruded buildings, roofs, 3D tiles, traffic heat
    const points: Layer[] = [];      // markers/gauges/incidents/POIs/labels — always above fills
    const top: Layer[] = [];         // device GPS pulse — above everything

    // ── Imagery (beneath all vector data) ─────────────────────────────────
    // Esri is the only satellite useful at city zoom — high-res tiles up to 19.
    // Other GIBS rasters are served via MapLibre <Source>/<Layer> (see below)
    // for reliable tile stretching; only Esri HD, Himawari (WMS), terrain stay here.
    if (enabledLayers.has("satellite-esri")) imagery.push(esriSatelliteLayer(1.0) as Layer);
    if (enabledLayers.has("satellite-terrain")) imagery.push(openTopoTerrainLayer(0.6) as Layer);
    if (enabledLayers.has("satellite-himawari")) imagery.push(himawariInfraredLayer(0.55) as Layer);

    // ── Fills: area polygons that must sit beneath the data they contextualize ─
    const showBoundaryFill =
      enabledLayers.has("municipality-boundary-fill") ||
      enabledLayers.has("municipality-boundary");
    const showBoundaryLine = enabledLayers.has("municipality-boundary-line") || showBoundaryFill;
    if ((showBoundaryLine || showBoundaryFill) && campus)
      fills.push(campusBoundaryLayer(campus, { filled: showBoundaryFill, stroked: showBoundaryLine }) as Layer);
    // GISTDA Land Use / Land Cover
    if (enabledLayers.has("gistda-landuse") && gistdaLandUse.data.length > 0) fills.push(gistdaLandUseLayer(gistdaLandUse.data) as Layer);
    // AlphaEarth EO polygons render beneath the vector signature layers.
    if (enabledLayers.has("alphaearth-landcover") && alphaLandcover) fills.push(alphaEarthLandcoverLayer(alphaLandcover) as Layer);
    if (enabledLayers.has("alphaearth-floodprone") && alphaFloodProne) fills.push(alphaEarthFloodProneLayer(alphaFloodProne) as Layer);
    if (showFloodScenario && floodScenario) fills.push(new PolygonLayer({
      id: "terrain-flood-scenario", data: floodScenario.cells,
      getPolygon: cell => cell.polygon,
      getFillColor: cell => cell.depthM > 2 ? [21, 88, 143, 170] : [47, 151, 193, 130],
      stroked: false, filled: true, pickable: false,
    }));
    if (enabledLayers.has("river-buffer") && riverBuffer) fills.push(riverBufferLayer(riverBuffer) as Layer);
    // Deep South security — aggregate choropleths, never points.
    if (enabledLayers.has("conflict-choropleth") && provinceBoundaries) fills.push(...(conflictChoroplethLayer(provinceBoundaries) as Layer[]));
    if (enabledLayers.has("poverty-choropleth") && districtBoundaries) fills.push(povertyChoroplethLayer(districtBoundaries) as Layer);
    // Heritage old-town district shading.
    if (heritage && enabledLayers.has("heritage-old-town")) {
      const districtFc = {
        type: "FeatureCollection" as const,
        features: heritage.features.filter(f => f.properties.kind === "old-town-district"),
      } as FeatureCollection<Polygon | MultiPolygon, HeritageFeatureProps>;
      const dist = oldTownDistrictLayer(districtFc);
      if (dist) fills.push(dist as Layer);
    }

    // ── Lines: roads, rivers, ring roads, transit, distance rings ─────────
    if (enabledLayers.has("road-network") && roads)
      lines.push(roadNetworkLayer(roads as unknown as FeatureCollection<LineString, ClassifiedRoadProps>) as Layer);
    if (enabledLayers.has("ring-roads") && ringRoads)
      lines.push(ringRoadsLayer(ringRoads as unknown as Parameters<typeof ringRoadsLayer>[0]) as Layer);
    if (enabledLayers.has("waterways") && waterways) lines.push(waterwaysLayer(waterways) as Layer);
    if (enabledLayers.has("transit-lines") && transitLines) lines.push(transitLinesLayer(transitLines) as Layer);
    if (enabledLayers.has("distance-grid")) lines.push(distanceGridLayer(YALA.center, [1, 5, 10]) as Layer);

    // ── Structures: extruded buildings + 3D tiles, then traffic heat above them ─
    if (enabledLayers.has("municipality-buildings") && memoizedBuildingsLayer)
      structures.push(memoizedBuildingsLayer);
    if (enabledLayers.has("municipality-buildings") && memoizedRoofsLayer)
      structures.push(memoizedRoofsLayer);
    // 3D Tiles pilot — OGC-standard streaming buildings (replaces extruded GeoJSON when available)
    if (tile3d.layer) structures.push(tile3d.layer as Layer);
    if (enabledLayers.has("traffic-heatmap") && trafficSamples.length > 0) structures.push(trafficHeatmapLayer(trafficSamples) as Layer);

    // ── Points: markers + gauges + incidents + POIs — always above fills ──
    if (enabledLayers.has("gistda-solar") && gistdaSolar.data.length > 0) points.push(gistdaSolarLayer(gistdaSolar.data) as Layer);
    if (enabledLayers.has("transit-stations") && transitStations) points.push(transitStationsLayer(transitStations) as Layer);
    if (enabledLayers.has("cctv-cameras")) points.push(cctvLayer(cctv.data) as Layer);
    if (enabledLayers.has("incidents-city-reports")) points.push(incidentLayer("incidents-city-reports", cityReports.data) as Layer);
    if (enabledLayers.has("incidents-itic")) points.push(incidentLayer("incidents-itic", iticEvents.data) as Layer);
    if (enabledLayers.has("datago-points") && datago.data.length > 0) points.push(datagoPointsLayer(datago.data) as Layer);
    // Air4Thai PCD stations (official Thai government AQ monitors for Yala province)
    if (enabledLayers.has("air4thai-stations") && air4thai.data.length > 0) points.push(air4thaiLayer(air4thai.data) as Layer);
    // GISTDA POI Digital Twin (authoritative Thai government POIs)
    if (enabledLayers.has("gistda-pois") && gistdaPois.data.length > 0) points.push(gistdaPoiLayer(gistdaPois.data) as Layer);
    // News pins — geocoded headlines so the mayor sees "which market" at a glance
    if (enabledLayers.has("news-pins") && news.data.length > 0) points.push(newsPinsLayer(news.data) as Layer);
    // Civic POIs (province-wide OSM: hospitals/schools/police/fire/temples/markets/...)
    if (enabledLayers.has("civic-points") && civicPoints) points.push(civicPointsLayer(civicPoints) as Layer);
    // Heritage temple spires.
    if (heritage && enabledLayers.has("heritage-temple-spires")) {
      const spiresFc = {
        type: "FeatureCollection" as const,
        features: heritage.features.filter(f => f.properties.kind !== "old-town-district"),
      } as FeatureCollection<Point, HeritageFeatureProps>;
      points.push(...(templeSpiresLayer(spiresFc) as Layer[]));
    }
    // Legacy point-incident layer retained for non-SEC contexts; SEC lens uses the choropleth.
    if (enabledLayers.has("conflict-incidents") && conflictIncidents.data.length > 0)
      points.push(conflictIncidentsLayer(conflictIncidents.data) as Layer);
    if (enabledLayers.has("security-news") && news.data.length > 0)
      points.push(securityNewsLayer(news.data) as Layer);
    // Flood gauges + Bang Lang Dam (API-backed)
    if (enabledLayers.has("flood-gauges") && floodGauges.data.length > 0)
      points.push(floodGaugesLayer(floodGauges.data) as Layer);
    if (enabledLayers.has("dam-status") && damStatus.data.length > 0)
      points.push(damStatusLayer(damStatus.data) as Layer);
    if (enabledLayers.has("shelters") && shelters.data.length > 0)
      points.push(sheltersLayer(shelters.data) as Layer);
    if (enabledLayers.has("municipal-flood-cameras") && floodCams.data.length > 0)
      points.push(municipalFloodCameraLayer(floodCams.data) as Layer);
    if (enabledLayers.has("flood-affected-districts") && floodAffectedPoints.length > 0)
      points.push(floodAffectedDistrictsLayer(floodAffectedPoints) as Layer);
    // Distance-grid labels ride above the rings.
    if (enabledLayers.has("distance-grid")) points.push(distanceGridLabelsLayer(YALA.center, [1, 5, 10]) as Layer);

    // ── Top: device GPS pulse — always above everything else ──────────────
    if (presence.lng != null && presence.lat != null) {
      const [accDisk, dot] = devicePresenceLayer(presence.lng, presence.lat, presence.accuracyM);
      top.push(accDisk as Layer, dot as Layer);
    }

    return [...imagery, ...fills, ...lines, ...structures, ...points, ...top];
  }, [
    theme,
    enabledLayers, showFloodScenario, floodScenario, is3D, isSubstructure, campus, buildings,
    memoizedBuildingsLayer, memoizedRoofsLayer,
    trafficSamples,
    transitStations, transitLines, roads,
    cctv.data, cityReports.data,
    iticEvents.data,
    civicPoints, waterways, heritage, datago.data,
    gistdaPois.data, gistdaSolar.data, gistdaLandUse.data, news.data,
    ringRoads, riverBuffer, alphaLandcover, alphaFloodProne,
    provinceBoundaries, districtBoundaries,
    conflictIncidents.data, floodGauges.data, damStatus.data, shelters.data, floodCams.data,
    floodAffectedPoints,
    presence.lng, presence.lat, presence.accuracyM,
    tile3d.layer,
  ]);

  // Feature counts — passed to LayerPalette so every toggle shows a number,
  // making it immediately obvious whether the layer has data or not.
  const layerCounts = useMemo(() => ({
    "municipality-boundary-line": campus?.features.length ?? 0,
    "municipality-boundary-fill": campus?.features.length ?? 0,
    "municipality-boundary":      campus?.features.length ?? 0,
    "municipality-buildings": buildings?.features.length ?? 0,
    "building-roofs":         buildings?.features.length ?? 0,
    "heritage-old-town":      heritage?.features.filter((f) => f.properties.kind === "old-town-district").length ?? 0,
    "heritage-temple-spires": heritage?.features.filter((f) => f.properties.kind !== "old-town-district").length ?? 0,
    "road-network":           roads?.features.length ?? 0,
    "transit-stations":       transitStations?.features.length ?? 0,
    "transit-lines":          transitLines?.features.length ?? 0,
    "civic-points":           civicPoints?.features.length ?? 0,
    "waterways":              waterways?.features.length ?? 0,
    "flood-risk-zones":       floodScenario?.cells.length ?? 0,
    "cctv-cameras":           cctv.data.length,
    "incidents-itic":         iticEvents.data.length,
    "incidents-city-reports": cityReports.data.length,
    "datago-points":          datago.data.length,
    "air4thai-stations":      air4thai.data.length,
    "gistda-pois":            gistdaPois.data.length,
    "gistda-solar":           gistdaSolar.data.length,
    "gistda-landuse":         gistdaLandUse.data.length,
    "news-pins":              news.data.filter((n) => n.lat != null).length,
    "ring-roads":             ringRoads?.features.length ?? 0,
    "river-buffer":           riverBuffer?.features.length ?? 0,
    "alphaearth-landcover":   alphaLandcover?.features.length ?? 0,
    "alphaearth-floodprone":  alphaFloodProne?.features.length ?? 0,
    "conflict-incidents":     conflictIncidents.data.length,
    "security-news":          news.data.filter((n) => n.lat != null && n.tags.includes("SEC")).length,
    "flood-gauges":           floodGauges.data.length,
    "dam-status":             damStatus.data.length,
    "shelters":               shelters.data.length,
    "municipal-flood-cameras": floodCams.data.length,
    "flood-affected-districts": floodAffectedPoints.length,
  } as Record<string, number>), [
    campus, buildings, roads, transitStations, transitLines, civicPoints, waterways,
    floodScenario, heritage,
    cctv.data, iticEvents.data, cityReports.data, datago.data,
    air4thai.data, gistdaPois.data, gistdaSolar.data, gistdaLandUse.data, news.data,
    ringRoads, riverBuffer, alphaLandcover, alphaFloodProne,
    conflictIncidents.data, floodGauges.data, damStatus.data, shelters.data, floodCams.data,
    floodAffectedPoints,
  ]);

  // Feed health per map layer — drives the "needs key" pill in the palette and
  // the toast on toggle. Only layers backed by a live feed appear here.
  const layerStatuses = useMemo<Partial<Record<LayerId, LayerStatus>>>(() => ({
    "cctv-cameras":           { tier: cctv.fallbackTier, note: cctv.note },
    "incidents-itic":         { tier: iticEvents.fallbackTier, note: iticEvents.note },
    "incidents-city-reports": { tier: cityReports.fallbackTier, note: cityReports.note },
    "datago-points":          { tier: datago.fallbackTier, note: datago.note },
    "air4thai-stations":      { tier: air4thai.fallbackTier, note: air4thai.note },
    "gistda-pois":            { tier: gistdaPois.fallbackTier, note: gistdaPois.note },
    "gistda-solar":           { tier: gistdaSolar.fallbackTier, note: gistdaSolar.note },
    "gistda-landuse":         { tier: gistdaLandUse.fallbackTier, note: gistdaLandUse.note },
    "news-pins":              { tier: news.fallbackTier, note: news.note },
    "security-news":          { tier: news.fallbackTier, note: news.note },
    "conflict-incidents":     { tier: conflictIncidents.fallbackTier, note: conflictIncidents.note },
    "flood-gauges":           { tier: floodGauges.fallbackTier, note: floodGauges.note },
    "dam-status":             { tier: damStatus.fallbackTier, note: damStatus.note },
    "shelters":               { tier: shelters.fallbackTier, note: shelters.note },
    "municipal-flood-cameras": { tier: floodCams.fallbackTier, note: floodCams.note },
    "flood-affected-districts": { tier: floodOps.fallbackTier, note: floodOps.note },
  }), [
    cctv.fallbackTier, cctv.note,
    iticEvents.fallbackTier, iticEvents.note, cityReports.fallbackTier, cityReports.note,
    datago.fallbackTier, datago.note, air4thai.fallbackTier, air4thai.note,
    gistdaPois.fallbackTier, gistdaPois.note,
    gistdaSolar.fallbackTier, gistdaSolar.note, gistdaLandUse.fallbackTier, gistdaLandUse.note,
    news.fallbackTier, news.note,
    conflictIncidents.fallbackTier, conflictIncidents.note,
    floodGauges.fallbackTier, floodGauges.note, damStatus.fallbackTier, damStatus.note,
    floodCams.fallbackTier, floodCams.note,
    shelters.fallbackTier, shelters.note,
    floodOps.fallbackTier, floodOps.note,
  ]);

  // Keep the toggle handler's view of feedback fresh without re-creating it.
  layerFeedbackRef.current = { counts: layerCounts, statuses: layerStatuses, enabled: enabledLayers };

  // Average GISTDA Solar irradiance across all buildings (kWh/m²/month)
  const avgSolarIrrKWh = useMemo(() => {
    const vals = gistdaSolar.data
      .map((b) => b.solarIrr)
      .filter((v): v is number => typeof v === "number" && v > 0);
    return vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null;
  }, [gistdaSolar.data]);

  const feedHealth = useMemo(() => [
    { label: "NEWS", tier: news.fallbackTier, ageMinutes: news.ageMinutes },
    { label: "CR", tier: cityReports.fallbackTier, ageMinutes: cityReports.ageMinutes },
    { label: "iTIC", tier: iticEvents.fallbackTier, ageMinutes: iticEvents.ageMinutes },
    { label: "CCTV", tier: cctv.fallbackTier, ageMinutes: cctv.ageMinutes },
    { label: "DATA", tier: datago.fallbackTier, ageMinutes: datago.ageMinutes },
    { label: "AQ", tier: aqiTrend.fallbackTier, ageMinutes: aqiTrend.ageMinutes },
    { label: "WX", tier: weather.fallbackTier, ageMinutes: weather.ageMinutes },
    { label: "EX", tier: executive.fallbackTier, ageMinutes: executive.ageMinutes },
    { label: "MK", tier: markets.fallbackTier, ageMinutes: markets.ageMinutes },
  ], [news.fallbackTier, news.ageMinutes, cityReports.fallbackTier, cityReports.ageMinutes, iticEvents.fallbackTier, iticEvents.ageMinutes, cctv.fallbackTier, cctv.ageMinutes, datago.fallbackTier, datago.ageMinutes, aqiTrend.fallbackTier, aqiTrend.ageMinutes, weather.fallbackTier, weather.ageMinutes, executive.fallbackTier, executive.ageMinutes, markets.fallbackTier, markets.ageMinutes]);

  return (
    <div
      className={`shell ${isMobile ? `mobile mobile-panel-${mobilePanel}` : ""}`}
      data-mobile={isMobile ? "true" : "false"}
    >
      {!online && (
        <div className="offline-banner mono" role="alert" aria-live="assertive">
          OFFLINE — feeds are stale until the connection returns
        </div>
      )}
      {toast && (
        <div className="layer-toast mono" role="status" aria-live="polite">
          {toast}
        </div>
      )}
      {/* ── Top bar ── */}
      <MemoTopBar        feeds={feedHealth}
        onOpenCatalog={useCallback(() => setCatalogOpen(true), [])}
        onOpenMayorsDesk={useCallback(() => setMayorsDeskOpen(true), [])}
        catalogCount={SOURCE_CATALOG.length}
        viewMode={viewMode}
        onCycleViewMode={cycleViewMode}
        onOpenManual={useCallback(() => setManualOpen(true), [])}
        onOpenWhitepaper={useCallback(() => setWhitepaperOpen(true), [])}
        onOpenAbout={useCallback(() => setAboutOpen(true), [])}
        onOpenAtlas={useCallback(() => setAtlasOpen(true), [])}
        onOpenPlatform={useCallback(() => setPlatformOpen(true), [])}
        onStartDemo={useCallback(() => setTourActive(true), [])}
        onFlip={onFlip}
        onOpenSheets={useCallback(() => setSheetsOpen(true), [])}
        sheetsConfigured={sheetsConfigured}
        academic={academic.data[0] ?? null}
        systemStatus={systemHealth?.system.status ?? "unknown"}
      />

      {/* Degraded system banner */}
      {systemHealth && systemHealth.system.status !== "healthy" && (
        <div className={`system-banner banner-${systemHealth.system.status}`}>
          <span className="mono">
            SYSTEM {systemHealth.system.status.toUpperCase()} — {" "}
            {systemHealth.system.down > 0 && `${systemHealth.system.down} adapter${systemHealth.system.down > 1 ? "s" : ""} down`}
            {systemHealth.system.down > 0 && systemHealth.system.degraded > 0 && ", "}
            {systemHealth.system.degraded > 0 && `${systemHealth.system.degraded} degraded`}
            {" · "}
            <a href={`${API_BASE}/api/health/detailed`} target="_blank" rel="noreferrer" className="banner-link">
              View details →
            </a>
          </span>
        </div>
      )}
      {!systemHealth && systemHealthError && (
        <div className="system-banner banner-down" role="status">
          <span className="mono">
            {isMobile ? t("Data API unreachable · feeds unavailable", "ติดต่อ API ไม่ได้ · ไม่มีข้อมูลสด") : (
              <>API HOST UNREACHABLE — {API_BASE.replace(/^https?:\/\//, "")} · {systemHealthError}</>
            )}
          </span>
        </div>
      )}

      {/* Headlines accompany the map; weather stays in the operational brief. */}
      {!isMobile && <MemoNewsTicker items={news.data} loading={news.fallbackTier === "loading"} />}

      {/* ── Left sidebar: the provincial brief.
          EXEC vs OPS share the same sidebar — the lens only changes the map
          layer set (EXEC = strategic, OPS = day-to-day). The legacy Chula
          ExecutiveBrief / StrategicAlerts / PeerComparison panels were built
          for a university and don't belong on a city mayor's desk. ── */}
      <aside className="left-bar" aria-hidden={isMobile && mobilePanel !== "brief"}>
        {/* ── Mobile only: the world strip (weather + clocks), feed health, and
            the secondary actions relocate here so the Map tab stays a clean,
            full-screen map. Reflowed into readable blocks by CSS. ── */}

        {lens === "intelligence" && (
          <div className="left-section" style={{ paddingBottom: 12 }}>
            <Suspense fallback={null}>
              <SituationDigest
                nasaReadings={nasaEarth.data[0] ?? null}
                avgSolarIrrKWh={avgSolarIrrKWh}
                forecastAlerts={forecastAlerts}
                forecasts={forecastMetrics}
              />
            </Suspense>
          </div>
        )}
        {lens === "executive" && (
          <div className="left-section" style={{ paddingBottom: 12 }}>
            <MemoExecutiveBriefing              executive={executive.data[0] ?? null}
              weather={weather.data[0] ?? null}
              airQuality={airQuality.data[0] ?? null}
              openIncidents={cityReports.data.filter((r) => r.status !== "resolved").length + iticEvents.data.length}
              reservoirs={reservoirs.data}
              markets={markets.data[0] ?? null}
              adapterHealth={systemHealth?.adapters}
              ageMinutes={executive.ageMinutes}
              fallbackTier={executive.fallbackTier === "loading" ? undefined : executive.fallbackTier}
            />
          </div>
        )}
        {/* ── ACTION BOARD — leads the rail. Every other panel reports numbers;
            this one turns them into ranked, actionable decisions. ── */}
        <div className="left-section left-section-divided brief-risk">
          <ActionBoard
            {...boardInput}
            loading={boardStillLoading}
          />
        </div>
        <div className="brief-weather">
            <MemoWorldStrip compact
              hostAqi={hostAqi}
              hostPm25={hostPm25}
              hostWeather={hostWeather}
              hostPulse={hostPulse}
              precipNowcast={precip.data[0] ?? null}
            />
        </div>
        {/* ── FLOOD-FIGHTING CLUSTER (promoted to the top of the rail) ──────────
            Flood is Yala's headline risk this year, so the live water/flood/air
            panels lead; static demographic KPIs are demoted below them. */}
        <div className="left-section left-section-divided brief-risk">
          <FloodBrief
            gauges={floodGauges.data}
            dam={damStatus.data[0] ?? null}
            precip={precip.data[0] ?? null}
            floodRiskFeatures={null}
            riverForecast={riverForecast.data[0] ?? null}
            shelters={shelters.data}
            ageMinutes={floodGauges.ageMinutes}
            fallbackTier={floodGauges.fallbackTier === "loading" ? undefined : floodGauges.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided brief-risk">
          <CctvFloodBrief
            cameras={floodCams.data}
            viewerUrl={CCTV_VIEWER_URL}
            ageMinutes={floodCams.ageMinutes}
            fallbackTier={floodCams.fallbackTier === "loading" ? undefined : floodCams.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided brief-risk">
          <YalaFloodOpsBrief
            data={floodOps.data[0] ?? null}
            loading={floodOps.fallbackTier === "loading"}
            ageMinutes={floodOps.ageMinutes}
            fallbackTier={floodOps.fallbackTier === "loading" ? undefined : floodOps.fallbackTier}
          />
        </div>
        {reservoirs.data.length > 0 && (
          <div className="left-section left-section-divided">
            <WaterPanel
              data={reservoirs.data}
              loading={reservoirs.fallbackTier === "loading"}
              ageMinutes={reservoirs.ageMinutes}
            />
          </div>
        )}
        <div className="left-section left-section-divided brief-risk">
          <AirDangerBrief
            provinces={airDanger.data}
            loading={airDanger.fallbackTier === "loading"}
            ageMinutes={airDanger.ageMinutes}
            fallbackTier={airDanger.fallbackTier === "loading" ? undefined : airDanger.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided brief-risk">
          <AqiBadge
            trend={aqiTrend.data[0] ?? null}
            station={aqicn.data[0]
              ? { aqi: aqicn.data[0].aqi, pm25: aqicn.data[0].pm25, name: aqicn.data[0].station, observedAt: aqicn.data[0].observedAt }
              : null}
            loading={aqiTrend.fallbackTier === "loading"}
          />
        </div>
        <div className="left-section left-section-divided brief-risk">
          <SecurityDesk
            incidents={conflictIncidents.data}
            timeline={securityTimeline.data[0] ?? null}
            digest={securityDigest.data[0] ?? null}
            secNews={securityFeed.data}
            status={securityStatus.data[0] ?? null}
            loading={conflictIncidents.fallbackTier === "loading"}
            incidentsAge={conflictIncidents.ageMinutes}
            incidentsTier={conflictIncidents.fallbackTier === "loading" ? undefined : conflictIncidents.fallbackTier}
            feedAge={securityFeed.ageMinutes}
            feedTier={securityFeed.fallbackTier === "loading" ? undefined : securityFeed.fallbackTier}
          />
        </div>
        {/* Static demographic KPIs — demoted below the live flood/air cluster. */}
        {provincialKPIs.data.length > 0 && (
          <div className="left-section">
            <ProvincialKPIs
              data={provincialKPIs.data[0] ?? null}
              loading={provincialKPIs.fallbackTier === "loading"}
              ageMinutes={provincialKPIs.ageMinutes}
              fallbackTier={provincialKPIs.fallbackTier === "loading" ? undefined : provincialKPIs.fallbackTier}
            />
          </div>
        )}
        <div className="left-section left-section-divided">
          <EarthAlphaBrief
            enabledLayers={enabledLayers}
            onToggleLayer={onToggleLayer}
            gistdaPoiCount={gistdaPois.data.length}
            gistdaSolarCount={gistdaSolar.data.length}
            gistdaLandUseCount={gistdaLandUse.data.length}
            floodZoneCount={floodScenario?.cells.length ?? 0}
            waterwayCount={waterways?.features.length ?? 0}
            openIncidentCount={cityReports.data.filter((r) => r.status !== "resolved").length + iticEvents.data.length}
            sheetsConfigured={sheetsConfigured}
            nasaReadings={nasaEarth.data[0] ?? null}
            avgSolarIrrKWh={avgSolarIrrKWh}
            ageMinutes={nasaEarth.ageMinutes}
            fallbackTier={nasaEarth.fallbackTier === "loading" ? undefined : nasaEarth.fallbackTier}
          />
        </div>
        <div className="left-section left-section-divided">
          <MemoPredictivePanel            apiBase={API_BASE}
            onMetricClick={handleForecastMetricClick}
            onAlert={handleForecastAlert}
            onForecastsLoaded={(f) => setForecastMetrics(f)}
          />
        </div>
        <div className="left-section">
          <span className="eyebrow mono">Municipal Brief</span>
          <MemoKpiStrip            cityReports={cityReports.data}
            iticEvents={iticEvents.data}
            airQuality={airQuality.data}
            weather={weather.data}
          />
        </div>
        {/* Mobile-only utility footer: feed health + secondary actions sink to the
            bottom (CSS order) so the BRIEF leads with the headline risks. */}
        {isMobile && (
          <div className="mobile-brief-bottom">
            <div className="mobile-brief-feeds" aria-label="Live data feed health">
              {feedHealth.map((f) => (
                <span
                  key={f.label}
                  className={`feed-chip feed-${f.tier}`}
                  title={`${f.tier} · ${f.ageMinutes}m ago`}
                >
                  <span className={`dot ${f.tier === "loading" ? "loading" : f.tier}`} />
                  {f.label}
                </span>
              ))}
            </div>
            <div className="mobile-brief-actions">
              <button className="mono" onClick={() => setCatalogOpen(true)}>SOURCES · {SOURCE_CATALOG.length}</button>
              <button className="mono" onClick={() => setAtlasOpen(true)}>◷ ATLAS</button>
              <button className="mono" onClick={() => setPlatformOpen(true)}>⌕ LEARN</button>
              <button className="mono about-toggle" onClick={() => setAboutOpen(true)}>ABOUT</button>
            </div>
          </div>
        )}
      </aside>

      {/* ── Map center — nothing overlaps this ── */}
      <main className="map-area" data-map-zoom={viewState.zoom.toFixed(2)} data-map-lens={lens} aria-hidden={isMobile && mobilePanel !== "map"}>
        <div className="map-host">
          <div className="sr-only" role="status" aria-live="polite">
            Yala map. {mapViewState.kind === "custom" ? mapViewState.label : `Current lens: ${LENSES.find((l) => l.id === lens)?.label ?? lens}`}.
            {enabledLayers.size} layers are currently enabled. Use Clean aerial view to inspect satellite imagery without data overlays.
          </div>
          <DeckGL
            viewState={viewState}
            onViewStateChange={handleViewStateChange}
            // Controller options — minZoom/maxZoom live in MapStateProps which is not
            // re-exported by @deck.gl/core, so we extend ControllerProps locally.
            // All three constraints enforce bounds *before* onViewStateChange fires,
            // preventing the one-frame "flash to outer space" that a pure React clamp cannot.
            // scrollZoom.speed: default 0.01 → one fast trackpad swipe jumps 5+ levels;
            // 0.004 is ~2.5× slower, still comfortable but safe.
            controller={{ minZoom: 13, maxZoom: 20, maxBounds: YALA.outerBounds, scrollZoom: { speed: 0.004, smooth: false } } as ControllerProps & { minZoom?: number; maxZoom?: number }}
            layers={layers}
            getTooltip={tooltipForPickMemo}
            onClick={handleMapClick}
            onHover={handleMapHover}
          >
            <MapLibreMap
              mapStyle={mapStyle}
              mapLib={maplibregl as unknown as typeof maplibregl}
              attributionControl={false}
              renderWorldCopies={false}
            >
              {/* GIBS satellite raster sources — rendered via MapLibre for
                  reliable tile stretching at any viewport zoom. Only active
                  layers are mounted so MapLibre only fetches what's needed.
                  `GIBS_RENDER` carries pre-built, stable {tiles, paint} refs
                  so React-render churn doesn't make MapLibre re-diff every frame. */}
              {GIBS_RENDER.filter(g => enabledLayers.has(g.id as LayerId)).map(g => (
                <Source
                  key={g.id}
                  id={`gibs-src-${g.id}`}
                  type="raster"
                  tiles={g.tiles as unknown as string[]}
                  tileSize={256}
                  minzoom={0}
                  maxzoom={g.maxzoom}
                >
                  <MapLayer
                    id={`gibs-lyr-${g.id}`}
                    type="raster"
                    paint={g.paint}
                    beforeId="labels-top"
                  />
                </Source>
              ))}
              {enabledLayers.has("satellite-esri") && (
                <Source
                  id="esri-world-imagery-src"
                  type="raster"
                  tiles={["https://services.arcgisonline.com/arcgis/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"]}
                  tileSize={256}
                  maxzoom={19}
                >
                  <MapLayer
                    id="esri-world-imagery"
                    type="raster"
                    paint={{ "raster-opacity": 1 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
              {enabledLayers.has("google-satellite") && googleSatSession && (
                <Source
                  id="google-satellite-src"
                  type="raster"
                  tiles={[googleTileTemplate(googleSatSession)]}
                  tileSize={256}
                  maxzoom={20}
                >
                  <MapLayer
                    id="google-satellite-lyr"
                    type="raster"
                    paint={{ "raster-opacity": 1 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
              {enabledLayers.has("google-traffic") && googleTrafficSession && (
                <Source
                  id="google-traffic-src"
                  type="raster"
                  tiles={[googleTileTemplate(googleTrafficSession)]}
                  tileSize={256}
                  maxzoom={20}
                >
                  <MapLayer
                    id="google-traffic-lyr"
                    type="raster"
                    paint={{ "raster-opacity": 0.85 }}
                    beforeId="labels-top"
                  />
                </Source>
              )}
            </MapLibreMap>
          </DeckGL>
          <BuildingSearch
            buildings={buildings}
            onSelect={(centroid, props) => {
              setSelectedBuilding(props);
              setSelectedCoord([centroid[0], centroid[1]]);
              flyTo(centroid[0], centroid[1], 17.5);
            }}
          />
          <MapOverlayControls
            mapViewState={mapViewState}
            onAerialOnly={setAerialOnly}
            onClearOverlays={clearOverlays}
            onRestoreLens={restoreLens}
          />
          {/* Map zoom controls — back-up for gesture / trackpad */}
          <div className="zoom-controls" aria-label="Map zoom controls">
            <button onClick={() => zoomBy(0.6)} aria-label="Zoom in" className="zoom-btn">+</button>
            <button onClick={() => zoomBy(-0.6)} aria-label="Zoom out" className="zoom-btn">−</button>
          </div>
          {showFloodScenario && <FloodScenarioControl compact={tourActive} terrain={terrain} rise={scenarioRise} onRise={setScenarioRise} exposed={floodScenario?.exposedIds.length ?? 0} unknown={floodScenario?.unknownBuildings ?? 0} />}
          {/* Compass — reflects bearing, click resets to north */}
          <MapCompass bearing={viewState.bearing} onResetNorth={resetNorth} />
          {/* Bottom-left HUD: building-type legend (when buildings are shown) + live coordinate readout */}
          <div className="map-hud-bl">
            {enabledLayers.has("municipality-buildings") && (lens === "vibes" ? <details className="roof-provenance"><summary>{buildings?.features.length.toLocaleString() ?? "—"} {t("roofed footprints · estimates", "อาคารพร้อมหลังคา · ค่าประมาณ")}</summary><p>{t("Satellite-assisted flat/gable estimates, not a roof survey. All source heights are approximately 6 m; ridge heights inferred. Complex footprints use flat defaults. Footprints are Microsoft/OSM within the operational bbox, not a certified municipal inventory.", "ประเมินหลังคาแบน/จั่วจากภาพดาวเทียม ไม่ใช่งานสำรวจ ความสูงพื้นฐานประมาณ 6 ม. สันหลังคาเป็นค่าประมาณ รูปทรงซับซ้อนใช้หลังคาแบน ข้อมูล Microsoft/OSM ภายในกรอบปฏิบัติการ ไม่ใช่ทะเบียนอาคารเทศบาลที่รับรองแล้ว")}</p><p>Imagery © Esri &amp; contributors · Microsoft / OSM footprints</p></details> : <BuildingLegend />)}
            <div className="coord-readout" aria-hidden="true">
              <span className="coord-label">⌖</span>
              <span className="coord-val" ref={coordRef}>—</span>
            </div>
          </div>
          <BuildingCard
            building={selectedBuilding}
            roof={selectedBuilding ? roofEvidence?.roofs[selectedBuilding.id] : undefined}
            coord={selectedCoord}
            onClose={() => setSelectedBuilding(null)}
          />
          <IncidentCard
            incident={selectedIncident}
            onClose={() => setSelectedIncident(null)}
          />
          {forecastAlerts.size > 0 && (
            <div style={{
              position: "absolute", top: 12, left: "50%", transform: "translateX(-50%)",
              zIndex: 20, display: "flex", gap: 6, pointerEvents: "none",
            }}>
              {[...forecastAlerts].map((m) => (
                <span key={m} className="mono eyebrow" style={{
                  background: "var(--bad)", color: "var(--bg)",
                  padding: "3px 8px", fontSize: "0.60rem", letterSpacing: "0.1em",
                }}>▲ {METRIC_LABEL[m] ?? m} FORECAST</span>
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Lens and layer choices lead; the news desk follows in the same rail. */}
      <aside className="right-bar" aria-hidden={isMobile && mobilePanel !== "layers"}>
        <div className="right-layers">
          <MemoLayerPalette            lens={lens}
            onLensChange={onLensChange}
            enabled={enabledLayers}
            onToggleLayer={onToggleLayer}
            counts={layerCounts}
            statuses={layerStatuses}
          />
        </div>
        <div className="right-news">
          <NewsDesk
            items={news.data}
            loading={news.fallbackTier === "loading"}
            ageMinutes={news.ageMinutes}
            onRefresh={news.refetch}
          />
          <div className="left-section-divided" style={{ marginTop: 4 }}>
            <FacebookPanel
              posts={facebook.data}
              loading={facebook.fallbackTier === "loading"}
              ageMinutes={facebook.ageMinutes}
              // The Meta page-plugin iframe is always live, so only surface a
              // tier chip when server-side tagged posts actually loaded. Showing
              // "unavailable" on a panel that's rendering a live timeline is
              // misleading — suppress it when the optional Graph feed is absent.
              fallbackTier={facebook.data.length > 0 && facebook.fallbackTier !== "loading" ? facebook.fallbackTier : undefined}
            />
          </div>
        </div>

      </aside>

      {/* ── Bottom bar: ident / counts ── */}
      <div className="bottom-bar">
        <div className="bottom-ident">
          <div className="bottom-ident-row">
            <span className="pill pill-standard" title="UNDP-JTC Digital Twins for Cities (Jul 2025) · ADB Digital Twin Framework (May 2025)">DT·L2</span>
            <span className="bottom-standard mono">UNDP · ADB</span>
          </div>
        </div>
        <div className="bottom-feeds" aria-label="Live data feed health">
          <span className="feeds-label">FEEDS</span>
          {feedHealth.map((f) => (
            <span
              key={f.label}
              className={`feed-chip feed-${f.tier}`}
              title={`${f.label} — ${f.tier} · ${f.ageMinutes}m ago`}
            >
              <span className={`dot ${f.tier === "loading" ? "loading" : f.tier}`} />
              {f.label}
            </span>
          ))}
        </div>
        <div className="bottom-stats">
          <span>{buildings?.features.length ?? 0} {t("BUILDINGS", "อาคาร")}</span>
          <span>{trafficSamples.length} {t("ROADS", "ถนน")}</span>
          <span>{layers.length} {t("LAYERS", "ชั้นข้อมูล")}</span>
          <span>{civicPoints?.features.length ?? 0} {t("CIVIC", "จุดบริการ")}</span>
          <span>{cctv.data.length} CCTV</span>
          <span>{gistdaPois.data.length} GISTDA</span>
        </div>
      </div>

      {/* Guided demo tour — auto-driving narrated walkthrough for presentations */}
      {tourActive && (
        <DemoTour
          scenes={TOUR_SCENES}
          onScene={applyTourScene}
          onExit={() => setTourActive(false)}
        />
      )}

      {/* Modals — lazy-loaded chunks, fetched only on first open */}
      <Suspense fallback={null}>
        {mayorsDeskOpen && (
          <MayorsDesk
            open={mayorsDeskOpen}
            onClose={() => setMayorsDeskOpen(false)}
            actions={deskActions}
            signals={deskSignals}
            news={news.data}
            loading={boardStillLoading}
          />
        )}
      </Suspense>
      <Suspense fallback={null}>
        {catalogOpen && <SourceCatalog open={catalogOpen} onClose={() => setCatalogOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>
        {manualOpen && <Manual open={manualOpen} onClose={() => setManualOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>
        {atlasOpen && <AtlasView onClose={() => setAtlasOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>
        {platformOpen && (
          <PlatformView
            onClose={() => setPlatformOpen(false)}
            onOpenLens={(id) => onLensChange(id as LensId)}
            onOpenAtlas={() => { setPlatformOpen(false); setAtlasOpen(true); }}
          />
        )}
      </Suspense>
      <AboutSheet
        open={aboutOpen}
        onClose={() => setAboutOpen(false)}
        onOpenResearch={() => { setAboutOpen(false); setWhitepaperOpen(true); }}
        onOpenCatalog={() => { setAboutOpen(false); setCatalogOpen(true); }}
        onOpenAtlas={() => { setAboutOpen(false); setAtlasOpen(true); }}
      />
      <Suspense fallback={null}>
        {whitepaperOpen && <Whitepaper open={whitepaperOpen} onClose={() => setWhitepaperOpen(false)} />}
      </Suspense>
      <Suspense fallback={null}>
        {sheetsOpen && (
          <SheetsPanel
            open={sheetsOpen}
            onClose={() => {
              setSheetsOpen(false);
              try { setSheetsConfigured(Boolean(localStorage.getItem(SHEETS_STORAGE_KEY))); } catch {}
            }}
          />
        )}
      </Suspense>
      <ChatBox apiBase={API_BASE} />
      {isMobile && <MobileNav panel={mobilePanel} onChange={setMobilePanel} />}
    </div>
  );
}
